import { useCallback, useEffect, useRef, useState } from "react";
import {
  getIsMobileDevice,
  isFirefox,
  isIOS,
} from "../utilities/device-detection";

declare global {
  // TypeScript's DOM lib in this workspace does not include the
  // VirtualKeyboard API yet. Keep this declaration intentionally narrow so the
  // hook only types the browser surface it actually reads.
  interface Navigator {
    readonly virtualKeyboard?: VirtualKeyboard;
  }

  interface VirtualKeyboard extends EventTarget {
    readonly boundingRect: DOMRect;
    overlaysContent: boolean;
  }
}

const MIN_KEYBOARD_HEIGHT_PX = 80;
const KEYBOARD_VISIBILITY_DEBOUNCE_MS = 50;

// These input types either do not accept text or do not normally open the
// virtual keyboard. Treating them as keyboard-capable would make the viewport
// fallback too eager during ordinary control interactions.
const NON_TEXT_INPUT_TYPES = new Set<string>([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function getReliableVirtualKeyboard(): VirtualKeyboard | undefined {
  // Chromium-based mobile browsers expose navigator.virtualKeyboard and fire a
  // geometrychange event with an explicit keyboard rectangle. Prefer that
  // signal where available because it is independent of page scroll position.
  //
  // iOS browsers and Firefox need the visualViewport fallback. iOS browsers do
  // not provide a reliable VirtualKeyboard implementation, and Firefox is kept
  // on the fallback path to avoid depending on partial or prefixed behavior.
  if (isIOS() || isFirefox()) {
    return undefined;
  }

  return navigator.virtualKeyboard;
}

function isEditableElement(element: Element | null): boolean {
  // The visualViewport can change for reasons unrelated to the keyboard, like
  // browser toolbar collapse or normal page scrolling. Only trust that fallback
  // while an element that can summon the virtual keyboard is focused.
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  if (element instanceof HTMLTextAreaElement) {
    return !element.disabled && !element.readOnly;
  }

  if (element instanceof HTMLInputElement) {
    return (
      !element.disabled &&
      !element.readOnly &&
      !NON_TEXT_INPUT_TYPES.has(element.type)
    );
  }

  return false;
}

function isKeyboardVisibleFromVisualViewport(
  minKeyboardHeightPx: number,
  wasVisible: boolean,
): boolean {
  const visualViewport = window.visualViewport;

  if (!visualViewport) {
    return false;
  }

  // Safari can blur before dismissal finishes. Wait for geometry to recover.
  if (!wasVisible && !isEditableElement(document.activeElement)) {
    return false;
  }

  // Safari can shrink innerHeight and pan offsetTop when focusing a field near
  // the end of a form. Neither measures keyboard height. The root clientHeight
  // stays tied to the layout viewport while the visual viewport is occluded.
  // Normalize pinch zoom so zooming alone is not mistaken for a keyboard.
  const coveredViewportHeight = Math.max(
    0,
    document.documentElement.clientHeight -
      visualViewport.height * visualViewport.scale,
  );

  return coveredViewportHeight > minKeyboardHeightPx;
}

function isVirtualKeyboardVisible(
  minKeyboardHeightPx: number,
  wasVisible: boolean,
): boolean {
  const virtualKeyboard = getReliableVirtualKeyboard();

  if (virtualKeyboard) {
    // This is the primary path for non-iOS/non-Firefox browsers. It avoids the
    // original bug where a fixed/sticky save bar at the bottom of the document
    // was mistaken for keyboard displacement during normal scrolling.
    if (virtualKeyboard.boundingRect.height > minKeyboardHeightPx) {
      return true;
    }

    // If the browser exposes VirtualKeyboard but reports no covered rectangle,
    // do not fall back unless an editable field is focused. That keeps a zero
    // virtual keyboard rect from turning normal viewport movement into a false
    // positive.
    if (!isEditableElement(document.activeElement)) {
      return false;
    }
  }

  return isKeyboardVisibleFromVisualViewport(minKeyboardHeightPx, wasVisible);
}

export function useIsVirtualKeyboardVisible(
  minKeyboardHeightPx = MIN_KEYBOARD_HEIGHT_PX,
  enabled = true,
): boolean {
  const [isVisible, setIsVisible] = useState(false);
  const debounceTimerRef = useRef<number | null>(null);

  const updateKeyboardVisibility = useCallback(() => {
    // Keyboard, focus, and viewport events often arrive in small bursts, and
    // blur/focusout can fire before the browser has finished restoring the
    // viewport. Debouncing the read gives layout a moment to settle and avoids
    // rapid hide/show flicker at the bottom of long forms.
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      setIsVisible((current) => {
        const next = isVirtualKeyboardVisible(minKeyboardHeightPx, current);
        if (current === next) {
          return current;
        }

        return next;
      });
    }, KEYBOARD_VISIBILITY_DEBOUNCE_MS);
  }, [minKeyboardHeightPx]);

  useEffect(() => {
    // Desktop devices never show a mobile virtual keyboard, and this hook is
    // mounted as part of the save bar even on desktop layouts. Bail out before
    // reading keyboard/viewport APIs or registering listeners so desktop usage
    // stays effectively inert.
    if (!enabled || !getIsMobileDevice()) {
      setIsVisible(false);
      return;
    }

    const virtualKeyboard = getReliableVirtualKeyboard();
    const visualViewport = window.visualViewport;

    // Run once on mount so the save bar reflects an already-open keyboard, for
    // example when this component mounts after a focused field is restored.
    updateKeyboardVisibility();

    // Focus events drive the visualViewport fallback. They also clear stale
    // visible state after the active editable field loses focus.
    window.addEventListener("focusin", updateKeyboardVisibility);
    window.addEventListener("focusout", updateKeyboardVisibility);

    // Window resize covers orientation changes and browsers that resize the
    // layout viewport when the keyboard opens.
    window.addEventListener("resize", updateKeyboardVisibility);
    window.addEventListener("pageshow", updateKeyboardVisibility);
    document.addEventListener("visibilitychange", updateKeyboardVisibility);

    // navigator.virtualKeyboard is the most direct signal where supported.
    virtualKeyboard?.addEventListener(
      "geometrychange",
      updateKeyboardVisibility,
    );

    // visualViewport is the fallback for iOS/Firefox and a secondary safety net
    // for browsers that expose VirtualKeyboard but still report keyboard-driven
    // viewport changes.
    visualViewport?.addEventListener("resize", updateKeyboardVisibility);
    visualViewport?.addEventListener("scroll", updateKeyboardVisibility);

    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      window.removeEventListener("focusin", updateKeyboardVisibility);
      window.removeEventListener("focusout", updateKeyboardVisibility);
      window.removeEventListener("resize", updateKeyboardVisibility);
      window.removeEventListener("pageshow", updateKeyboardVisibility);
      document.removeEventListener(
        "visibilitychange",
        updateKeyboardVisibility,
      );
      virtualKeyboard?.removeEventListener(
        "geometrychange",
        updateKeyboardVisibility,
      );
      visualViewport?.removeEventListener("resize", updateKeyboardVisibility);
      visualViewport?.removeEventListener("scroll", updateKeyboardVisibility);
    };
  }, [enabled, updateKeyboardVisibility]);

  return enabled && isVisible;
}
